import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { 
  TrendingUp, 
  Database, 
  FileText,
  Package,
  Loader2,
  RefreshCw,
  ArrowUp,
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle,
  MessageSquare,
  Send,
  Bot
} from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mini sparkline component
const Sparkline = ({ data, color = "#1E8B5A", height = 30 }) => {
  if (!data || data.length < 2) return <div style={{ height }} />;
  
  const values = data.map(d => d.cumulative_total || d.value || 0);
  const max = Math.max(...values);
  const min = Math.min(...values) * 0.9;
  const range = max - min || 1;
  
  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * 100;
    const y = 100 - ((v - min) / range) * 80 - 10;
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <svg viewBox="0 0 100 100" className="w-full" style={{ height }} preserveAspectRatio="none">
      <defs>
        <linearGradient id={`grad-${color.replace('#', '')}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.05" />
        </linearGradient>
      </defs>
      <polygon
        points={`0,100 ${points} 100,100`}
        fill={`url(#grad-${color.replace('#', '')})`}
      />
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// Alert component
const AlertItem = ({ alert, onDismiss, onAnalyze }) => {
  const severityConfig = {
    high: { 
      icon: AlertTriangle, 
      bg: "bg-emerald-50", 
      border: "border-emerald-300",
      iconColor: "text-emerald-600",
      textColor: "text-emerald-800"
    },
    medium: { 
      icon: AlertCircle, 
      bg: "bg-green-50", 
      border: "border-green-200",
      iconColor: "text-green-600",
      textColor: "text-green-800"
    },
    low: { 
      icon: Info, 
      bg: "bg-teal-50", 
      border: "border-teal-200",
      iconColor: "text-teal-600",
      textColor: "text-teal-800"
    },
    info: { 
      icon: CheckCircle, 
      bg: "bg-cyan-50", 
      border: "border-cyan-200",
      iconColor: "text-cyan-500",
      textColor: "text-cyan-800"
    }
  };
  
  const config = severityConfig[alert.severity] || severityConfig.info;
  const Icon = config.icon;
  
  return (
    <div className={`${config.bg} ${config.border} border rounded-lg p-4 flex items-start gap-3 transition-all hover:shadow-sm`}>
      <Icon className={`h-5 w-5 ${config.iconColor} flex-shrink-0 mt-0.5`} />
      <p className={`${config.textColor} text-sm flex-1 leading-relaxed`}>
        {alert.message}
      </p>
      <div className="flex gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onAnalyze(alert)}
          className="h-7 px-2 text-purple-500 hover:text-purple-700 hover:bg-purple-50"
          title="Analyser avec l'IA"
        >
          <MessageSquare className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDismiss(alert.id)}
          className="h-7 px-2 text-gray-400 hover:text-gray-600 hover:bg-white/50"
        >
          OK
        </Button>
      </div>
    </div>
  );
};

export default function PricingStats() {
  const { getAuthHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Chat modal state
  const [chatOpen, setChatOpen] = useState(false);
  const [chatAlert, setChatAlert] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);

  const handleAnalyzeAlert = async (alert) => {
    setChatAlert(alert);
    setChatOpen(true);
    setChatMessages([]);
    setChatLoading(true);
    
    // Initial prompt to AI
    const initialPrompt = `Voici une alerte de notre système de tarification que tu n'as pas réussi à corriger automatiquement :

"${alert.message}"

Peux-tu m'expliquer :
1. Pourquoi cette anomalie n'a pas pu être corrigée automatiquement ?
2. Quelles informations supplémentaires te manquent ?
3. Comment puis-je résoudre ce problème manuellement ?`;

    try {
      const response = await axios.post(
        `${API}/chat/alert-analysis`,
        { alert_id: alert.id, message: initialPrompt, alert_data: alert },
        { headers: getAuthHeaders() }
      );
      
      setChatMessages([
        { role: "user", content: initialPrompt },
        { role: "assistant", content: response.data.response }
      ]);
    } catch (error) {
      setChatMessages([
        { role: "user", content: initialPrompt },
        { role: "assistant", content: "Désolé, je n'ai pas pu analyser cette alerte. Erreur: " + (error.response?.data?.detail || error.message) }
      ]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleSendChatMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;
    
    const userMessage = chatInput;
    setChatInput("");
    setChatMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setChatLoading(true);
    
    try {
      const response = await axios.post(
        `${API}/chat/alert-analysis`,
        { 
          alert_id: chatAlert?.id, 
          message: userMessage, 
          alert_data: chatAlert,
          history: chatMessages 
        },
        { headers: getAuthHeaders() }
      );
      
      setChatMessages(prev => [...prev, { role: "assistant", content: response.data.response }]);
    } catch (error) {
      setChatMessages(prev => [...prev, { 
        role: "assistant", 
        content: "Erreur: " + (error.response?.data?.detail || error.message) 
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/stats/pricing/detailed`,
        { headers: getAuthHeaders() }
      );
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch pricing stats:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getAuthHeaders]);

  const fetchAlerts = useCallback(async () => {
    setAlertsLoading(true);
    try {
      const response = await axios.get(
        `${API}/stats/alerts`,
        { headers: getAuthHeaders() }
      );
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error("Failed to fetch alerts:", error);
    } finally {
      setAlertsLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchStats();
    fetchAlerts();
  }, [fetchStats, fetchAlerts]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchStats();
    fetchAlerts();
  };

  const handleDismissAlert = async (alertId) => {
    try {
      await axios.post(
        `${API}/stats/alerts/${alertId}/dismiss`,
        {},
        { headers: getAuthHeaders() }
      );
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch (error) {
      console.error("Failed to dismiss alert:", error);
      // Dismiss locally anyway for better UX
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1E8B5A]" />
        </div>
      </Layout>
    );
  }

  const latestGrowth = stats?.references_growth?.slice(-1)[0];
  const previousGrowth = stats?.references_growth?.slice(-2, -1)[0];
  const growthDiff = latestGrowth && previousGrowth 
    ? latestGrowth.cumulative_total - previousGrowth.cumulative_total 
    : 0;

  // Top 5 catégories
  const topCategories = stats?.by_category?.slice(0, 5) || [];

  return (
    <Layout>
      <div className="space-y-4 fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              Statistiques
            </h1>
            <p className="text-sm text-gray-500">
              {stats?.overview?.total_documents || 0} documents • {stats?.overview?.total_entries || 0} références
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={handleRefresh} 
              variant="outline"
              size="sm"
              disabled={refreshing}
              data-testid="refresh-stats-btn"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Stats Cards Row - Compact */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <Database className="h-5 w-5 text-[#1E8B5A]" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Références</p>
                <p className="text-xl font-bold">{stats?.overview?.total_entries || 0}</p>
              </div>
            </div>
            {growthDiff > 0 && (
              <div className="mt-2 flex items-center text-xs text-green-600">
                <ArrowUp className="h-3 w-3 mr-1" />
                +{growthDiff} ce mois
              </div>
            )}
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Documents</p>
                <p className="text-xl font-bold">{stats?.overview?.total_documents || 0}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <Package className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Catégories</p>
                <p className="text-xl font-bold">{stats?.overview?.categories_count || 0}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-100">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Alertes</p>
                <p className="text-xl font-bold">{alerts.length}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Main Content - Growth (3/4) + Top Categories (1/4) side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Growth Chart - 3/4 width */}
          {stats?.references_growth?.length > 0 && (
            <Card className="lg:col-span-3">
              <CardHeader className="py-3 px-4 border-b">
                <CardTitle className="text-sm flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-[#1E8B5A]" />
                  Évolution du nombre de références tarifaires enregistrées dans la base
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <Sparkline data={stats.references_growth} height={100} />
                <div className="flex justify-between text-xs text-gray-400 mt-2">
                  <span>{stats.references_growth[0]?.period}</span>
                  <span className="font-medium text-gray-700">
                    {stats.references_growth[stats.references_growth.length - 1]?.cumulative_total} total
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Top Categories - 1/4 width */}
          {topCategories.length > 0 && (
            <Card className="lg:col-span-1">
              <CardHeader className="py-3 px-4 border-b">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Package className="h-4 w-4 text-purple-600" />
                  Top catégories
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3">
                <div className="space-y-2">
                  {topCategories.map((cat, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div 
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: ['#1E8B5A', '#3B82F6', '#8B5CF6', '#F59E0B', '#EC4899'][idx] }}
                      />
                      <span className="text-xs text-gray-600 flex-1 truncate">{cat.name}</span>
                      <span className="text-xs font-medium text-gray-800">{cat.count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Alerts Section */}
        <Card>
          <CardHeader className="py-3 px-4 border-b">
            <CardTitle className="text-sm flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-orange-600" />
              Alertes et recommandations
              {alerts.length > 0 && (
                <span className="ml-auto text-xs font-normal bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                  {alerts.length} alerte{alerts.length > 1 ? 's' : ''}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {alertsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                <span className="ml-2 text-sm text-gray-500">Analyse en cours...</span>
              </div>
            ) : alerts.length > 0 ? (
              <div className="space-y-3">
                {alerts.map((alert) => (
                  <AlertItem 
                    key={alert.id} 
                    alert={alert} 
                    onDismiss={handleDismissAlert}
                    onAnalyze={handleAnalyzeAlert}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-400" />
                <p className="text-sm">Aucune alerte pour le moment</p>
                <p className="text-xs mt-1">Toutes les données semblent cohérentes</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Chat Modal for Alert Analysis */}
      <Dialog open={chatOpen} onOpenChange={setChatOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-purple-600" />
              Analyse IA de l'alerte
            </DialogTitle>
          </DialogHeader>
          
          {chatAlert && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-800">
              {chatAlert.message}
            </div>
          )}
          
          <div className="flex-1 overflow-y-auto space-y-4 py-4 min-h-[300px]">
            {chatMessages.map((msg, idx) => (
              <div 
                key={idx} 
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div 
                  className={`max-w-[80%] rounded-lg p-3 ${
                    msg.role === 'user' 
                      ? 'bg-[#1E8B5A] text-white' 
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {msg.role === 'user' ? (
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  ) : (
                    <div className="text-sm prose prose-sm max-w-none prose-headings:text-gray-800 prose-p:text-gray-700 prose-li:text-gray-700 prose-strong:text-gray-900">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {chatLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-lg p-3">
                  <Loader2 className="h-5 w-5 animate-spin text-purple-600" />
                </div>
              </div>
            )}
          </div>
          
          <div className="flex gap-2 pt-2 border-t">
            <Input
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSendChatMessage()}
              placeholder="Poser une question sur cette alerte..."
              disabled={chatLoading}
            />
            <Button 
              onClick={handleSendChatMessage} 
              disabled={chatLoading || !chatInput.trim()}
              className="bg-[#1E8B5A] hover:bg-[#166B45]"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
